import cacheService from './cacheService';

class CacheInitializer {
  constructor() {
    this.isInitialized = false;
    this.initPromise = null;
  }

  /**
   * Initialize essential caches that are needed on most pages
   */
  async initializeEssentialData() {
    if (this.isInitialized || this.initPromise) {
      return this.initPromise;
    }

    console.log('🚀 Initializing essential cache data...');
    
    this.initPromise = this.loadEssentialData();
    await this.initPromise;
    this.isInitialized = true;
    
    return this.initPromise;
  }

  /**
   * Load essential data that's needed on most pages
   */
  async loadEssentialData() {
    try {
      // Load core data that's used everywhere (amenities, POIs)
      const coreDataPromises = [
        cacheService.getAmenities().catch(err => console.warn('Failed to cache amenities:', err)),
        cacheService.getPOIs().catch(err => console.warn('Failed to cache POIs:', err)),
        cacheService.getPublicItineraries().catch(err => console.warn('Failed to cache public itineraries:', err)),
      ];

      // Load navbar data (weather, exchange rates)
      const navbarDataPromises = [
        cacheService.getWeather().catch(err => console.warn('Failed to cache weather:', err)),
        cacheService.getExchangeRates().catch(err => console.warn('Failed to cache exchange rates:', err)),
      ];

      // Load all essential data in parallel
      await Promise.allSettled([...coreDataPromises, ...navbarDataPromises]);
      
      console.log('✅ Essential cache data loaded');
    } catch (error) {
      console.error('❌ Error loading essential cache data:', error);
    }
  }

  /**
   * Initialize user-specific data (only call when user is authenticated)
   */
  async initializeUserData() {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('🔐 User not authenticated, skipping user data cache');
      return;
    }

    console.log('👤 Initializing user-specific cache data...');
    
    try {
      // Load user-specific data in parallel
      const userDataPromises = [
        cacheService.getFavorites().catch(err => console.warn('Failed to cache favorites:', err)),
        cacheService.getProfile().catch(err => console.warn('Failed to cache profile:', err)),
        cacheService.getMyTrips().catch(err => console.warn('Failed to cache trips:', err)),
      ];

      await Promise.allSettled(userDataPromises);
      console.log('✅ User cache data loaded');
    } catch (error) {
      console.error('❌ Error loading user cache data:', error);
    }
  }

  /**
   * Initialize all cache data (essential + user)
   */
  async initializeAllData() {
    await Promise.all([
      this.initializeEssentialData(),
      this.initializeUserData()
    ]);
  }

  /**
   * Reset initialization state (useful for logout)
   */
  reset() {
    this.isInitialized = false;
    this.initPromise = null;
  }

  /**
   * Get initialization status
   */
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      isInitializing: !!this.initPromise && !this.isInitialized
    };
  }
}

// Export singleton instance
const cacheInitializer = new CacheInitializer();
export default cacheInitializer; 